import { sortBy } from "lodash";
import { useMemo, useRef, useState } from "react";
import {
  AssessmentGroupListFragmentFragment,
  AssessmentType,
} from "types/graphql-schema";

import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import ComboboxGeneric, {
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";

import AssessmentGroupSidebar from "../assessment-group-sidebar/assessment-group-sidebar";
import { questionCountLabel } from "../helpers";
import { QuestionRow } from "./question-tab-section";

// The question set's row on the Questions tab. Every review needs one, so the
// row is required, and it can be swapped for another set as well as removed.
const QuestionSetSelector = ({
  assessmentType,
  availableAssessmentGroupSelectOptions,
  canUpdate,
  onUpdateAssessmentTemplate,
  onAssessmentGroupCreated,
  onAssessmentGroupArchived,
  selectedAssessmentGroup,
  error,
}: {
  assessmentType: AssessmentType;
  availableAssessmentGroupSelectOptions: ComboboxGenericOption<number>[];
  canUpdate: boolean;
  onUpdateAssessmentTemplate: (assessmentGroupId: number | null) => void;
  onAssessmentGroupCreated: (
    newAssessmentGroup: AssessmentGroupListFragmentFragment,
  ) => void;
  onAssessmentGroupArchived: (assessmentGroupId: number) => void;
  selectedAssessmentGroup: AssessmentGroupListFragmentFragment | null;
  error?: string;
}) => {
  // What the question set drawer is open for. Creating opens it empty, so the
  // chosen set stays chosen if the admin closes the drawer without saving.
  const [sidebarMode, setSidebarMode] = useState<
    "create" | "edit" | "duplicate" | null
  >(null);
  // The row opens the Add menu too, by pressing its button, like the rows
  // whose Add opens settings. The menu sets its button's ref itself, so this
  // holds the wrapper and finds the button inside.
  const addMenuRef = useRef<HTMLDivElement>(null);
  const options = useMemo(
    () =>
      sortBy(availableAssessmentGroupSelectOptions, (opt) =>
        opt.label.toLocaleLowerCase(),
      ),
    [availableAssessmentGroupSelectOptions],
  );

  return (
    <>
      {sidebarMode && (
        <AssessmentGroupSidebar
          assessmentType={assessmentType}
          assessmentGroupId={
            sidebarMode === "create" ? null : selectedAssessmentGroup?.id
          }
          assessmentGroupToDuplicateId={
            sidebarMode === "duplicate" ? selectedAssessmentGroup?.id : null
          }
          onClose={() => setSidebarMode(null)}
          onAssessmentGroupCreated={(assessmentGroup) => {
            onAssessmentGroupCreated(assessmentGroup);
            onUpdateAssessmentTemplate(assessmentGroup.id);
          }}
          onAssessmentGroupArchived={onAssessmentGroupArchived}
        />
      )}
      <QuestionRow
        title={selectedAssessmentGroup?.title ?? "Your Custom Question Set"}
        required
        summary={
          selectedAssessmentGroup
            ? `Custom question set · ${questionCountLabel(
                selectedAssessmentGroup.questionCount,
              )}`
            : "Write your own questions, or reuse a question set."
        }
        error={error}
        added={!!selectedAssessmentGroup}
        disabled={!canUpdate}
        onAdd={() => addMenuRef.current?.querySelector("button")?.click()}
        onEdit={() => setSidebarMode("edit")}
        onRemove={() => onUpdateAssessmentTemplate(null)}
        editAriaLabel="Edit question set"
        removeAriaLabel="Remove question set"
        actions={
          <>
            <ComboboxGeneric
              aria-label={`Question set selector: ${assessmentType}`}
              header="Change to"
              options={options}
              value={null}
              placement="bottom-end"
              containerClassName="flex"
              createLabel="Create new question set"
              onClickCreateNew={() => setSidebarMode("create")}
              onChangeValue={(opt) => onUpdateAssessmentTemplate(opt.value)}
            >
              <ComboboxGeneric.Button
                aria-label="Change question set"
                tooltip="Change question set"
                theme={buttonTheme.iconGray}
                icon={TopicflowIconMap.switchHorizontal}
                disabled={!canUpdate}
              />
            </ComboboxGeneric>
            <Button
              aria-label="Duplicate question set"
              tooltip="Duplicate question set"
              disabled={!canUpdate}
              icon={TopicflowIconMap.copy}
              theme={buttonTheme.iconGray}
              onClick={() => setSidebarMode("duplicate")}
            />
          </>
        }
        addActions={
          <div ref={addMenuRef} className="flex">
            <ComboboxGeneric
              aria-label={`Question set selector: ${assessmentType}`}
              header="Existing question sets"
              options={options}
              value={null}
              placement="bottom-end"
              // Hugs the button, so the menu lines up with its right edge.
              containerClassName="flex"
              createLabel="Create new question set"
              onClickCreateNew={() => setSidebarMode("create")}
              onChangeValue={(opt) => onUpdateAssessmentTemplate(opt.value)}
            >
              <ComboboxGeneric.Button
                aria-label={`Add question set: ${assessmentType}`}
                theme={buttonTheme.text}
                size={ButtonSize.medium}
                disabled={!canUpdate}
              >
                <TopicflowIcon type="plus" className="h-4 w-4" />
                Add
              </ComboboxGeneric.Button>
            </ComboboxGeneric>
          </div>
        }
      />
    </>
  );
};

export default QuestionSetSelector;
